'use client';

import { useEffect, useState } from 'react';
import { ChevronRight, FileText } from 'lucide-react';
import { Icon } from '@/components/ui/Icon';
import { BubbleActions } from '@/components/chat/BubbleBlock';
import { SendPreview } from '@/components/chat/blocks/SendPreview';
import { CHAT_CARD_DECIDED_EVENT, decideFromChat, type ChatCardDecision } from '@/lib/chat/block-actions';
import {
  DOCUMENT_CARD_CHANGE_TEXT,
  applianceLabel,
  documentCardSentence,
  outcomeLabel,
  type DocumentCardBlock,
} from '@/lib/chat/blocks/document-card';

/**
 * A certificate ready to go: what it is, what it says, and the email waiting
 * to take it (`document-card.ts`).
 *
 * The top half opens the PDF. The bottom half is the staged send's own
 * approval — the recipient and words the card STORED, and an orange tap that
 * approves THAT card (`decideFromChat`). Change asks Mucka in words for a
 * different recipient, which goes back through the ordinary send path and its
 * recipient checks; nothing here edits an address.
 *
 * No thumbnail: nothing renders a certificate's first page, so this is a
 * document glyph, not a drawing that pretends to be the PDF.
 */
export function DocumentCard({ block, live, onAnswer, onOpen }: {
  block: DocumentCardBlock;
  live: boolean;
  onAnswer: (text: string) => void;
  onOpen: (path: string) => void;
}) {
  // A decision made elsewhere — the plain card's own button, or a yes in words
  // that the panel reports — so this copy cannot sit on a live Send button
  // after the card has gone.
  const [decided, setDecided] = useState<ChatCardDecision | null>(null);
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    const onDecided = (e: Event) => {
      const detail = (e as CustomEvent<{ id?: string; decision?: ChatCardDecision }>).detail;
      if (detail?.id === block.actionId && detail.decision) setDecided(detail.decision);
    };
    window.addEventListener(CHAT_CARD_DECIDED_EVENT, onDecided);
    return () => window.removeEventListener(CHAT_CARD_DECIDED_EVENT, onDecided);
  }, [block.actionId]);

  const { send } = block;
  const heading = [block.shortLabel, block.postcode].filter(Boolean).join(' · ');
  const result = block.outcome
    ? [outcomeLabel(block.outcome), ...(block.applianceCount != null ? [applianceLabel(block.applianceCount)] : [])].join(' · ')
    : null;
  const who = block.signedBy ? `Signed by ${block.signedBy}` : block.engineer ? `Engineer: ${block.engineer}` : null;

  return (
    <div className="mchat-doc">
      <p className="mchat-doc-lead">{documentCardSentence(block)}</p>
      <button
        type="button"
        className="mchat-doc-open"
        aria-label={`Open the PDF: ${block.title}${block.serial ? ` ${block.serial}` : ''}`}
        onClick={() => onOpen(block.openPath)}
      >
        <span className="mchat-doc-glyph" aria-hidden="true"><Icon icon={FileText} size={22} /></span>
        <span className="mchat-doc-main">
          <span className="mchat-doc-title">{heading}</span>
          {result && <span className={`mchat-doc-pill mchat-doc-pill--${block.outcome}`}>{result}</span>}
          {!result && block.applianceCount != null && (
            <span className="mchat-doc-meta">{applianceLabel(block.applianceCount)}</span>
          )}
          {who && <span className="mchat-doc-meta">{who}</span>}
          {block.dueAgainLabel && <span className="mchat-doc-meta">Due again {block.dueAgainLabel}</span>}
          {block.lastSentLabel && <span className="mchat-doc-meta">Last emailed {block.lastSentLabel}</span>}
          <span className="mchat-doc-go">Open the PDF <Icon icon={ChevronRight} size={14} /></span>
        </span>
      </button>
      {decided === 'approved' ? (
        <BubbleActions actions={[]} done={`Sent to ${send.name}`} />
      ) : decided ? (
        <p className="mchat-doc-note">Not sent. That email was set aside, so nothing has gone to {send.name}.</p>
      ) : (
        <SendPreview
          what={`Email “${send.subject}” with the ${block.shortLabel} attached`}
          to={`${send.name} · ${send.email}`}
          words={send.words}
          verb={`Send to ${send.name}`}
          doneLabel={`Sent to ${send.name}`}
          laterLabel={changing ? 'Asked' : 'Change'}
          onLater={() => { setChanging(true); onAnswer(DOCUMENT_CARD_CHANGE_TEXT); }}
          onApprove={() => decideFromChat(block.actionId, 'approved')}
          live={live && !changing}
        />
      )}
    </div>
  );
}
